import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import Loader from '../components/Loader';
import { fetchCoverage, buildCoverageChips, nextStepFor } from '../components/EntryFlowModal';
import { MonthLedger } from '../components/MonthLedger';
import { X } from '../components/v2/Icons';
import { EntryStepper } from '../components/v2/EntryStepper';
import { CategoryContext } from '../components/v2/CategoryContext';
import { PageHeader, Card, Button, Field, Input, Select, UnitInput, Callout, Empty } from '../components/v2/ui';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Non-React module scope: labels resolve from the stored language directly
// (same pattern as utils/auth.js). Option VALUES stay unchanged.
const L = (sq, en) => (localStorage.getItem('k360-lang') === 'en' ? en : sq);

// Real industrial-process types (keys + per-tonne factors match config/process-factors.js on the API)
const PROCESS_TYPES = [
  { value: 'cement_clinker', label: L('Prodhimi i çimentos / klinkerit', 'Cement / clinker production') },
  { value: 'lime', label: L('Prodhimi i gëlqeres', 'Lime production') },
  { value: 'glass', label: L('Prodhimi i qelqit', 'Glass production') },
  { value: 'ceramics_brick', label: L('Prodhimi i qeramikës / tullave', 'Ceramics / brick production') },
  { value: 'steel', label: L('Prodhimi i çelikut', 'Steel production') },
  { value: 'aluminium', label: L('Prodhimi i aluminit', 'Aluminium production') },
  { value: 'soda_ash', label: L('Përdorimi i sodës kalcinuese (Soda Ash)', 'Soda ash use') },
  { value: 'limestone', label: L('Përdorimi i gurit gëlqeror (Limestone)', 'Limestone use') },
  { value: 'dolomite', label: L('Përdorimi i Dolomitit', 'Dolomite use') }
];

export const Scope1ProcessPage = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [formData, setFormData] = useState({
    buildingId: '',
    processType: '',
    fuelKey: '',
    period: getCurrentMonth(),
    quantity: '10',
    unit: 'tonne'
  });

  const [facilities, setFacilities] = useState([]);
  const [, setFuels] = useState([]);
  const [calculated, setCalculated] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [uploadedInvoice, setUploadedInvoice] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [buildingValidationError, setBuildingValidationError] = useState('');
  const [, setSummary] = useState({ current: null, previous: null });
  // Gating: does the company have industrial process emissions? null = not answered yet
  const [processApplicable, setProcessApplicable] = useState(null);
  // Verifiko → Ruaj → Vazhdo dialog
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('manual');
  const [flowError, setFlowError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  useEffect(() => {
    if (!uploadedFile) { setDocUrl(null); return undefined; }
    const url = URL.createObjectURL(uploadedFile);
    setDocUrl(url);
    return () => URL.revokeObjectURL(url); // avoid leaking blob URLs across uploads
  }, [uploadedFile]);
  const [ledgerKey, setLedgerKey] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/buildings`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setFacilities(data);
        } else {
          console.error('Buildings API returned non-array:', data);
          setFacilities([]);
        }
      })
      .catch(err => {
        console.error('Failed to fetch facilities:', err);
        setFacilities([]);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/fuels`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setFuels(data);
        } else {
          console.error('Fuels API returned non-array:', data);
          setFuels([]);
        }
      })
      .catch(err => {
        console.error('Failed to fetch fuels:', err);
        setFuels([]);
      });
  }, []);

  const fetchSummary = async (period) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/emissions/scope1/process/summary?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  useEffect(() => {
    if (formData.period) {
      fetchSummary(formData.period);
    }
  }, [formData.period]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsSaved(false);
  };

  /**
   * Attach the production log. Rewritten to take the file directly: the old
   * version built its own <input> and opened a picker, which the stepper's drop
   * zone already does — and a drag-and-drop would have opened a second dialog.
   */
  const handleUploadInvoice = async (picked) => {
    if (!picked) return;
    if (!formData.buildingId) {
      setBuildingValidationError(t('entry.uploadNoBuilding'));
      return;
    }
    if (picked.size > 10 * 1024 * 1024) {
      setBuildingValidationError(t('entry.uploadTooBig'));
      return;
    }
    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', picked);
      uploadFormData.append('buildingId', formData.buildingId);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/scope1/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }
      setUploadedInvoice(await response.json());
      setUploadedFile(picked);
      setBuildingValidationError('');
    } catch (err) {
      setBuildingValidationError(t('entry.uploadFailed', { message: err.message }));
    } finally {
      setUploading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setCalculating(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/emissions/scope1/process/calc`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          processType: formData.processType,
          period: formData.period,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit
        })
      });

      if (!response.ok) throw new Error('Calculation failed');
      const data = await response.json();
      setCalculated(data);
      setIsSaved(false);
      // Verifiko → Ruaj → Vazhdo: open the dialog right after the calculation
      setFlowError('');
      setStep(2);
    } catch (err) {
      alert(t('s3p.error', { message: err.message }));
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async () => {
    if (!calculated) return false;
    if (!formData.buildingId) {
      setBuildingValidationError(t('s3p.confirmBuilding'));
      return false;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        buildingId: parseInt(formData.buildingId),
        processType: formData.processType,
        period: formData.period,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        energy_kWh: calculated.energy_kWh,
        emissions_tCO2e: calculated.emissions_tCO2e
      };
      
      // Attach invoice if uploaded
      if (uploadedInvoice) {
        payload.invoiceId = uploadedInvoice.invoiceId;
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        alert(t('s1dj.sessionExpired'));
        window.location.href = '/';
        return false;
      }

      const response = await fetch(`${API_BASE}/api/emissions/scope1/process/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Save failed');
      }
      setIsSaved(true);
      setBuildingValidationError('');
      return true;
    } catch (err) {
      setBuildingValidationError(t('s3p.error', { message: err.message }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Dialog: save → coverage chips → success phase
  const handleFlowSave = async () => {
    setFlowError('');
    const ok = await handleSave();
    if (!ok) {
      setFlowError(t('s3p.saveFailedCheckBuilding'));
      return;
    }
    try {
      setCoverage(await fetchCoverage(formData.period));
    } catch (e) {
      setCoverage(null);
    }
    setLedgerKey(k => k + 1);
  };

  const resetEntry = () => {
    setCalculated(null);
    setUploadedInvoice(null);
    setUploadedFile(null);
    setIsSaved(false);
    setFormData(prev => ({ ...prev, processType: '', quantity: '' }));
    fetchSummary(formData.period);
  };

  const handleFlowAddAnother = () => { resetEntry(); setStep(1); };
  const handleFlowFinish = () => { resetEntry(); setStep(0); };

  const isCalculateDisabled = !formData.processType ||
    !formData.period || !formData.quantity || parseFloat(formData.quantity) <= 0 || calculating;

  const isBusy = calculating || saving;

  // Gating step — most companies have no industrial process emissions, so ask first.
  const selectedProcess = PROCESS_TYPES.find(p => p.value === formData.processType);

  /* The applicability gate stays: process emissions apply to a minority of
     companies, and asking once is kinder than presenting a form most users
     should leave empty. It now uses the v2 chrome instead of its own page. */
  if (processApplicable !== 'yes') {
    return (
      <ErrorBoundary>
        <Shell>
          <div className="k2-wrap">
            <PageHeader title={t('proc.title')} desc={t('proc.desc')} />
            <div style={{ maxWidth: 760 }}>
              <Card>
                {processApplicable === null ? (
                  <>
                    <p style={{ fontSize: 14, color: 'var(--n800)', lineHeight: 1.6, marginTop: 0, marginBottom: 8 }}>
                      {t('proc.applicableQ')}
                    </p>
                    <p style={{ fontSize: 12.5, color: 'var(--n500)', marginBottom: 20 }}>
                      {t('proc.applicableHint')}
                    </p>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <Button variant="mint" icon={X.Check} onClick={() => setProcessApplicable('yes')}>
                        {t('proc.yes')}
                      </Button>
                      <Button onClick={() => setProcessApplicable('no')}>{t('proc.no')}</Button>
                    </div>
                  </>
                ) : (
                  <Empty
                    icon={X.Check}
                    title={t('proc.notIdentified')}
                    body={t('proc.notIdentifiedBody')}
                    action={<Button onClick={() => setProcessApplicable(null)}>{t('proc.changeAnswer')}</Button>}
                  />
                )}
              </Card>
            </div>
          </div>
        </Shell>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Shell>
        <div className="k2-wrap">
          {isBusy && <Loader />}

          <PageHeader title={t('proc.title')} desc={t('proc.desc')}>
            <Button variant="gh" onClick={() => setProcessApplicable(null)}>{t('proc.changeAnswer')}</Button>
            <Button variant="gh" icon={X.Doc} onClick={() => navigate('/records')}>{t('entry.viewRecords')}</Button>
          </PageHeader>

          <EntryStepper
            scope={1}
            step={step}
            setStep={setStep}
            facilities={facilities}
            siteId={formData.buildingId}
            onSite={(e) => { handleInputChange('buildingId', e.target.value); setBuildingValidationError(''); }}
            siteError={buildingValidationError}
            /* scope1_process_records has no activity_purpose column. */
            purposes={[]}
            period={formData.period}
            mode={mode}
            setMode={setMode}
            ocrSupported={false}
            docLabel={t('proc.doc')}
            file={uploadedFile}
            onFile={(f) => handleUploadInvoice(f)}
            uploading={uploading}
            uploadNote={uploadedInvoice ? (
              <div style={{ marginTop: 10 }}><Callout tone="ok">{t('entry.uploadOk')}</Callout></div>
            ) : null}
            onCalculate={handleCalculate}
            calcDisabled={isCalculateDisabled}
            calcLabel={calculating ? t('entry.calculating') : t('entry.calc')}
            reviewRows={[
              { label: t('entry.building'), value: (facilities.find(f => String(f.id) === String(formData.buildingId)) || {}).name || '—' },
              { label: t('proc.type'), value: (selectedProcess || {}).label || '—' },
              { label: t('stationary.quantity'), value: `${formData.quantity} ${formData.unit}` },
              { label: t('entry.period'), value: formData.period },
              /* The "Faktori efektiv" row is gone — factors are not shown on entry screens. */
              { label: t('entry.document'), value: uploadedFile ? uploadedFile.name : t('entry.notAttached') }
            ]}
            result={{ value: calculated ? Number(calculated.emissions_tCO2e).toFixed(3) : '0.000', unit: t('common.tco2e') }}
            saving={saving}
            error={flowError}
            onSave={handleFlowSave}
            saved={isSaved}
            successText={calculated
              ? t('proc.success', { value: Number(calculated.emissions_tCO2e).toFixed(3), period: formData.period })
              : ''}
            coverage={buildCoverageChips('scope1', coverage)}
            nextStep={nextStepFor('scope1')}
            onAddAnother={handleFlowAddAnother}
            onFinish={handleFlowFinish}
            context={
              <>
                {step === 2 && docUrl && (
                  <Card title={t('elec.sourceDoc')} sub={uploadedFile ? uploadedFile.name : ''} bodyProps={{ style: { padding: 0 } }}>
                    <div style={{ borderTop: '1px solid var(--n200)', maxHeight: 420, overflow: 'auto' }}>
                      {uploadedFile && uploadedFile.type === 'application/pdf' ? (
                        <iframe title={t('elec.sourceDoc')} src={docUrl} style={{ width: '100%', height: 420, border: 0 }} />
                      ) : (
                        <img alt={t('elec.sourceDoc')} src={docUrl} style={{ width: '100%', display: 'block' }} />
                      )}
                    </div>
                  </Card>
                )}
                <CategoryContext
                  recordTypes={['scope1-process']}
                  categoryLabel={t('proc.title')}
                  facilities={facilities}
                  refreshKey={ledgerKey}
                  onPickSite={(id) => { handleInputChange('buildingId', String(id)); setStep(1); }}
                />
                <MonthLedger
                  period={formData.period}
                  recordTypes={['scope1-process']}
                  buildings={facilities}
                  refreshKey={ledgerKey}
                  emptyHint={t('proc.ledgerEmpty')}
                  onChanged={() => { fetchSummary(formData.period); setLedgerKey(k => k + 1); }}
                />
              </>
            }
          >
            <Field label={t('proc.type')} required>
              <Select value={formData.processType} onChange={(e) => handleInputChange('processType', e.target.value)}>
                <option value="">{t('proc.selectType')}</option>
                {PROCESS_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Select>
            </Field>

            <div className="k2-g2">
              <Field label={t('stationary.startMonth')} required>
                <Input type="month" value={formData.period} onChange={(e) => handleInputChange('period', e.target.value)} />
              </Field>

              <Field label={t('stationary.quantity')} required>
                <UnitInput
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  unit={formData.unit}
                />
              </Field>
            </div>
          </EntryStepper>
        </div>
      </Shell>
    </ErrorBoundary>
  );
};
